import { App } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { BodyOf } from "@/shared/api";
import { apiPatch, apiPost, apiUpload, errorMessage, qk } from "@/shared/api";
import type { AdminRobot } from "@/entities/robot";
import type { paths } from "@/api/types";

export type RobotCreateBody = BodyOf<paths["/api/v1/admin/robots"]["post"]>;
export type RobotUpdateBody = BodyOf<paths["/api/v1/admin/robots/{robot_id}"]["patch"]>;
export type RobotOrderBody = BodyOf<paths["/api/v1/admin/robots/order"]["post"]>;
export type OfferResetBody = BodyOf<
  paths["/api/v1/admin/robots/{robot_id}/offer/reset"]["post"]
>;

export function useCreateRobot() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (body: RobotCreateBody) => apiPost("/api/v1/admin/robots", { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.robots() });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      void message.error(errorMessage(error));
    },
  });
}

export function useUpdateRobot(id: number) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (body: RobotUpdateBody) =>
      apiPatch("/api/v1/admin/robots/{robot_id}", { params: { robot_id: id }, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.robots() });
      void queryClient.invalidateQueries({ queryKey: qk.robot(id) });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      void message.error(errorMessage(error));
    },
  });
}

/**
 * Перезапуск лимитированной кампании.
 *
 * Отдельная мутация, а не поле формы, по той же причине, по которой на бэкенде
 * это отдельная ручка: обнуление уже собранной суммы — событие, а не правка
 * поля, и в журнале действий оно читается своей строкой. Поэтому же
 * инвалидируется `audit`: оператор, нажавший «перезапустить», должен увидеть
 * запись, а не гадать, осталась ли она.
 */
export function useResetOffer(id: number) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (body: OfferResetBody) =>
      apiPost("/api/v1/admin/robots/{robot_id}/offer/reset", {
        params: { robot_id: id },
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.robots() });
      void queryClient.invalidateQueries({ queryKey: qk.robot(id) });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      void message.error(errorMessage(error));
    },
  });
}

/**
 * Загрузка картинки робота. Отдельная мутация, а не поле формы: файл уходит
 * multipart-ручкой, у которой id робота в пути.
 *
 * Поэтому у нового робота она вызывается ПОСЛЕ создания — до сохранения id
 * ещё нет. Блокировать загрузку до первого сохранения хуже: оператор заведёт
 * робота и уйдёт, не поняв, что картинку так и не приложил.
 */
export function useUploadRobotImage() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      apiUpload<AdminRobot>("/api/v1/admin/robots/{robot_id}/image", {
        params: { robot_id: id },
        file,
      }),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: qk.robots() });
      void queryClient.invalidateQueries({ queryKey: qk.robot(id) });
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (error) => {
      void message.error(errorMessage(error));
    },
  });
}

/**
 * Порядок карточек каталога (ТЗ §31).
 *
 * Обновление оптимистичное: оператор жмёт стрелку несколько раз подряд, и
 * полсекунды ожидания на каждый клик дают прыгающий под курсором список.
 * Гонки при этом безопасны — каждый запрос несёт полный целевой порядок, и
 * побеждает последний ответ, а не последний отправленный запрос.
 */
export function useReorderRobots() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationKey: ["robots", "order"],
    mutationFn: (body: RobotOrderBody) => apiPost("/api/v1/admin/robots/order", { body }),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: qk.robots() });
      const snapshot = queryClient.getQueryData<AdminRobot[]>(qk.robots());
      if (snapshot) {
        const byId = new Map(snapshot.map((robot) => [robot.id, robot]));
        queryClient.setQueryData<AdminRobot[]>(
          qk.robots(),
          body.ids.flatMap((id) => byId.get(id) ?? []),
        );
      }
      return { snapshot };
    },
    onError: (error, _body, context) => {
      if (context?.snapshot) queryClient.setQueryData(qk.robots(), context.snapshot);
      void message.error(errorMessage(error));
    },
    onSuccess: (data) => {
      // Ответ несёт каталог целиком, но записывать его можно только когда
      // других перестановок в полёте нет: иначе поздний ответ первого клика
      // затёр бы результат второго.
      if (queryClient.isMutating({ mutationKey: ["robots", "order"] }) <= 1) {
        queryClient.setQueryData(qk.robots(), data);
      }
      void queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}
